// src/components/profile/UserProfile.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  FormControlLabel,
  Checkbox,
  FormGroup,
  TextField,
  Chip,
  Autocomplete,
  Slider,
  Alert,
  Paper,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  PersonOutline,
  TravelExplore,
  Interests,
  Settings,
  CheckCircle,
} from '@mui/icons-material';
import { useAuthStore } from '../../stores/authStore';
import { CompleteUserProfile, DEFAULT_USER_PROFILE } from '../../types/profile';
import { useT } from '../../i18n/useT';

interface UserProfileProps {
  onClose?: () => void;
}

const COUNTRIES = [
  'United States', 'Canada', 'United Kingdom', 'Australia', 'Germany', 'France', 
  'Spain', 'Italy', 'Japan', 'South Korea', 'China', 'India', 'Brazil', 'Mexico',
  'Netherlands', 'Sweden', 'Norway', 'Switzerland', 'Austria', 'Belgium',
  // Add more countries as needed
];

const LANGUAGES = [
  'English', 'Spanish', 'French', 'German', 'Italian', 'Portuguese', 'Dutch',
  'Chinese', 'Japanese', 'Korean', 'Russian', 'Arabic', 'Hindi', 'Swedish',
  'Norwegian', 'Danish', 'Finnish', 'Polish', 'Turkish', 'Greek',
];

export const UserProfile: React.FC<UserProfileProps> = ({ onClose }) => {
  const t = useT();
  const { profile, updateUserProfile } = useAuthStore();
  const [activeStep, setActiveStep] = useState(0);
  const [userProfile, setUserProfile] = useState<CompleteUserProfile>(DEFAULT_USER_PROFILE);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // Surfaced when handleSave fails. Without this, a save error from
  // Firestore (e.g. rule rejection, network blip) would be swallowed
  // by the catch block and the dialog would silently stay open with
  // no indication that anything went wrong.
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (profile?.travelProfile) {
      setUserProfile(profile.travelProfile);
    }
  }, [profile]);

  // Pre-fill primaryLanguage from the language already chosen at the
  // language gate. We try a few likely localStorage keys so this stays
  // robust to future renames in the gate hook.
  useEffect(() => {
    if (userProfile.demographics.primaryLanguage) return;
    const ISO_TO_NAME: Record<string, string> = {
      en: 'English', es: 'Spanish', fr: 'French', de: 'German',
      it: 'Italian', pt: 'Portuguese', nl: 'Dutch', zh: 'Chinese',
      ja: 'Japanese', ko: 'Korean', ru: 'Russian', ar: 'Arabic',
      hi: 'Hindi', sv: 'Swedish', no: 'Norwegian', da: 'Danish',
      fi: 'Finnish', pl: 'Polish', tr: 'Turkish', el: 'Greek',
    };
    let code: string | null = null;
    for (const k of [
      'instant-tours.language',
      'app-language',
      'language',
      'i18n.language',
    ]) {
      const v = typeof window !== 'undefined' ? localStorage.getItem(k) : null;
      if (v) { code = v.toLowerCase().slice(0, 2); break; }
    }
    if (code && ISO_TO_NAME[code]) {
      setUserProfile((prev) => ({
        ...prev,
        demographics: { ...prev.demographics, primaryLanguage: ISO_TO_NAME[code!] },
      }));
    }
  }, [userProfile.demographics.primaryLanguage]);


  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError(null);
    try {
      const updatedProfile = {
        ...userProfile,
        lastUpdated: new Date(),
        completedAt: new Date(),
      };

      // We only need to send `travelProfile` — `updateUserProfile`
      // already merges with currentProfile and sets `lastActive`.
      // Sending `...profile` here was redundant and risked carrying
      // stale top-level fields back to Firestore.
      await updateUserProfile({
        travelProfile: updatedProfile,
      });

      // Save succeeded — dismiss the dialog. If we get here without
      // throwing, the store profile is updated and Firestore is
      // written. The parent's onClose flips showOnboarding to false.
      onClose?.();
    } catch (error) {
      // Surface the failure so the user sees something happened
      // instead of the dialog appearing frozen. Common causes:
      // Firestore rules rejection, network failure, auth expired.
      console.error('Failed to save profile:', error);
      const message =
        error instanceof Error
          ? error.message
          : 'We couldn\'t save your profile. Please try again.';
      setSaveError(message);
    } finally {
      setIsSaving(false);
    }
  };

  const updateProfile = (section: keyof CompleteUserProfile, updates: any) => {
    setUserProfile(prev => ({
      ...prev,
      [section]: { ...prev[section], ...updates },
    }));
  };

  const toggleArrayItem = (section: keyof CompleteUserProfile, field: string, value: string) => {
    setUserProfile(prev => {
      const currentArray = (prev[section] as any)[field] || [];
      const newArray = currentArray.includes(value)
        ? currentArray.filter((item: string) => item !== value)
        : [...currentArray, value];
      
      return {
        ...prev,
        [section]: {
          ...prev[section],
          [field]: newArray,
        },
      };
    });
  };

  const steps = [
    {
      label: t('Demographics'),
      icon: <PersonOutline />,
      content: (
        <Box sx={{ mt: 2 }}>
          <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
            <FormLabel component="legend">{t("Age Range")}</FormLabel>
            <RadioGroup
              value={userProfile.demographics.ageRange || ''}
              onChange={(e) => updateProfile('demographics', { ageRange: e.target.value })}
            >
              <FormControlLabel value="under-18" control={<Radio />} label={t("Under 18")} />
              <FormControlLabel value="18-25" control={<Radio />} label="18-25" />
              <FormControlLabel value="26-35" control={<Radio />} label="26-35" />
              <FormControlLabel value="36-45" control={<Radio />} label="36-45" />
              <FormControlLabel value="46-55" control={<Radio />} label="46-55" />
              <FormControlLabel value="56-65" control={<Radio />} label="56-65" />
              <FormControlLabel value="over-65" control={<Radio />} label={t("Over 65")} />
            </RadioGroup>
          </FormControl>

          <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
            <FormLabel component="legend">{t("Gender (Optional)")}</FormLabel>
            <RadioGroup
              value={userProfile.demographics.gender || ''}
              onChange={(e) => updateProfile('demographics', { gender: e.target.value })}
            >
              <FormControlLabel value="male" control={<Radio />} label={t("Male")} />
              <FormControlLabel value="female" control={<Radio />} label={t("Female")} />
              <FormControlLabel value="non-binary" control={<Radio />} label={t("Non-binary")} />
              <FormControlLabel value="prefer-not-to-say" control={<Radio />} label={t("Prefer not to say")} />
            </RadioGroup>
          </FormControl>

          <Box sx={{ mb: 3 }}>
            <Autocomplete
              value={userProfile.demographics.homeCountry}
              onChange={(event, newValue) => updateProfile('demographics', { homeCountry: newValue })}
              options={COUNTRIES}
              renderInput={(params) => <TextField {...params} label={t("Home Country")} />}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Autocomplete
              value={userProfile.demographics.primaryLanguage}
              onChange={(event, newValue) => updateProfile('demographics', { primaryLanguage: newValue })}
              options={LANGUAGES}
              renderInput={(params) => <TextField {...params} label={t("Primary Language")} />}
            />
          </Box>
        </Box>
      ),
    },
    {
      label: t('Travel Style'),
      icon: <TravelExplore />,
      content: (
        <Box sx={{ mt: 2 }}>
          <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
            <FormLabel component="legend">{t("Travel Style (Select all that apply)")}</FormLabel>
            <FormGroup>
              {[
                { value: 'luxury',      label: t('Luxury') },
                { value: 'budget',      label: t('Budget') },
                { value: 'adventure',   label: t('Adventure') },
                { value: 'cultural',    label: t('Cultural') },
                { value: 'relaxation',  label: t('Relaxation') },
                { value: 'eco-tourism', label: t('Eco tourism') },
              ].map(({ value: style, label }) => (
                <FormControlLabel
                  key={style}
                  control={
                    <Checkbox
                      checked={userProfile.travelPreferences.travelStyle.includes(style as any)}
                      onChange={() => toggleArrayItem('travelPreferences', 'travelStyle', style)}
                    />
                  }
                  label={label}
                />
              ))}
            </FormGroup>
          </FormControl>

          <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
            <FormLabel component="legend">{t("Preferred Pace")}</FormLabel>
            <RadioGroup
              value={userProfile.travelPreferences.pace || ''}
              onChange={(e) => updateProfile('travelPreferences', { pace: e.target.value })}
            >
              <FormControlLabel value="fast" control={<Radio />} label={t("Fast - Cover more in less time")} />
              <FormControlLabel value="moderate" control={<Radio />} label={t("Moderate - Balanced approach")} />
              <FormControlLabel value="slow" control={<Radio />} label={t("Slow - Immersive experiences")} />
            </RadioGroup>
          </FormControl>

          <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
            <FormLabel component="legend">{t("Food Preferences (Select all that apply)")}</FormLabel>
            <FormGroup>
              {[
                { value: 'vegetarian',    label: t('Vegetarian') },
                { value: 'vegan',         label: t('Vegan') },
                { value: 'local-cuisine', label: t('Local cuisine') },
                { value: 'fine-dining',   label: t('Fine dining') },
                { value: 'street-food',   label: t('Street food') },
              ].map(({ value: food, label }) => (
                <FormControlLabel
                  key={food}
                  control={
                    <Checkbox
                      checked={userProfile.travelPreferences.foodPreferences.includes(food as any)}
                      onChange={() => toggleArrayItem('travelPreferences', 'foodPreferences', food)}
                    />
                  }
                  label={({"vegetarian":t("Vegetarian"),"vegan":t("Vegan"),"local-cuisine":t("Local cuisine"),"fine-dining":t("Fine dining"),"street-food":t("Street food")} as Record<string,string>)[food]}
                />
              ))}
            </FormGroup>
          </FormControl>

          <TextField
            fullWidth
            label={t("Dietary Restrictions or Allergies")}
            value={userProfile.travelPreferences.dietaryRestrictions || ''}
            onChange={(e) => updateProfile('travelPreferences', { dietaryRestrictions: e.target.value })}
            multiline
            rows={2}
            sx={{ mb: 3 }}
          />
        </Box>
      ),
    },
    {
      label: t('Interests'),
      icon: <Interests />,
      content: (
        <Box sx={{ mt: 2 }}>
          <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
            <FormLabel component="legend">{t("Cultural Interests")}</FormLabel>
            <FormGroup>
              {[
                { value: 'museums',          label: t('Museums') },
                { value: 'history',          label: t('History') },
                { value: 'art',              label: t('Art') },
                { value: 'architecture',     label: t('Architecture') },
                { value: 'local-traditions', label: t('Local traditions') },
              ].map(({ value: interest, label }) => (
                <FormControlLabel
                  key={interest}
                  control={
                    <Checkbox
                      checked={userProfile.interests.cultural.includes(interest as any)}
                      onChange={() => toggleArrayItem('interests', 'cultural', interest)}
                    />
                  }
                  label={({"museums":t("Museums"),"history":t("History"),"art":t("Art"),"architecture":t("Architecture"),"local-traditions":t("Local traditions")} as Record<string,string>)[interest]}
                />
              ))}
            </FormGroup>
          </FormControl>

          <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
            <FormLabel component="legend">{t("Nature & Outdoors")}</FormLabel>
            <FormGroup>
              {[
                { value: 'hiking',     label: t('Hiking') },
                { value: 'beaches',    label: t('Beaches') },
                { value: 'safaris',    label: t('Safaris') },
                { value: 'camping',    label: t('Camping') },
                { value: 'stargazing', label: t('Stargazing') },
                { value: 'wildlife',   label: t('Wildlife') },
              ].map(({ value: nature, label }) => (
                <FormControlLabel
                  key={nature}
                  control={
                    <Checkbox
                      checked={userProfile.interests.nature.includes(nature as any)}
                      onChange={() => toggleArrayItem('interests', 'nature', nature)}
                    />
                  }
                  label={({"hiking":t("Hiking"),"beaches":t("Beaches"),"safaris":t("Safaris"),"camping":t("Camping"),"stargazing":t("Stargazing"),"wildlife":t("Wildlife")} as Record<string,string>)[nature]}
                />
              ))}
            </FormGroup>
          </FormControl>

          <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
            <FormLabel component="legend">{t("Urban Exploration")}</FormLabel>
            <FormGroup>
              {[
                { value: 'nightlife',   label: t('Nightlife') },
                { value: 'shopping',    label: t('Shopping') },
                { value: 'restaurants', label: t('Restaurants') },
                { value: 'markets',     label: t('Markets') },
                { value: 'street-art',  label: t('Street art') },
              ].map(({ value: urban, label }) => (
                <FormControlLabel
                  key={urban}
                  control={
                    <Checkbox
                      checked={userProfile.interests.urban.includes(urban as any)}
                      onChange={() => toggleArrayItem('interests', 'urban', urban)}
                    />
                  }
                  label={({"nightlife":t("Nightlife"),"shopping":t("Shopping"),"restaurants":t("Restaurants"),"markets":t("Markets"),"street-art":t("Street art")} as Record<string,string>)[urban]}
                />
              ))}
            </FormGroup>
          </FormControl>
        </Box>
      ),
    },
    {
      label: t('Practical Info'),
      icon: <Settings />,
      content: (
        <Box sx={{ mt: 2 }}>
          <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
            <FormLabel component="legend">{t("Budget Range")}</FormLabel>
            <RadioGroup
              value={userProfile.constraints.budgetRange || ''}
              onChange={(e) => updateProfile('constraints', { budgetRange: e.target.value })}
            >
              <FormControlLabel value="budget" control={<Radio />} label={t("Budget ($0-50/day)")} />
              <FormControlLabel value="mid-range" control={<Radio />} label={t("Mid-range ($50-150/day)")} />
              <FormControlLabel value="luxury" control={<Radio />} label={t("Luxury ($150+/day)")} />
              <FormControlLabel value="no-limit" control={<Radio />} label={t("No specific limit")} />
            </RadioGroup>
          </FormControl>

          <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
            <FormLabel component="legend">{t("Time Availability")}</FormLabel>
            <RadioGroup
              value={userProfile.constraints.timeAvailability || ''}
              onChange={(e) => updateProfile('constraints', { timeAvailability: e.target.value })}
            >
              <FormControlLabel value="short-trips" control={<Radio />} label={t("Short trips (1-3 days)")} />
              <FormControlLabel value="week-long" control={<Radio />} label={t("Week-long trips")} />
              <FormControlLabel value="extended" control={<Radio />} label={t("Extended travel (2+ weeks)")} />
              <FormControlLabel value="flexible" control={<Radio />} label={t("Flexible schedule")} />
            </RadioGroup>
          </FormControl>

          <TextField
            fullWidth
            label={t("Accessibility Needs")}
            value={userProfile.constraints.accessibilityNeeds || ''}
            onChange={(e) => updateProfile('constraints', { accessibilityNeeds: e.target.value })}
            multiline
            rows={2}
            sx={{ mb: 3 }}
            placeholder={t("Mobility requirements, medical considerations, etc.")}
          />

          <TextField
            fullWidth
            label={t("Safety Considerations")}
            value={userProfile.constraints.safetyConsiderations || ''}
            onChange={(e) => updateProfile('constraints', { safetyConsiderations: e.target.value })}
            multiline
            rows={2}
            sx={{ mb: 3 }}
            placeholder={t("Comfort levels, areas to avoid, etc.")}
          />
        </Box>
      ),
    },
  ];

  const isStepComplete = (stepIndex: number): boolean => {
    switch (stepIndex) {
      case 0:
        return !!userProfile.demographics.ageRange && !!userProfile.demographics.homeCountry;
      case 1:
        return userProfile.travelPreferences.travelStyle.length > 0 && !!userProfile.travelPreferences.pace;
      case 2:
        return userProfile.interests.cultural.length > 0 || 
               userProfile.interests.nature.length > 0 || 
               userProfile.interests.urban.length > 0;
      case 3:
        return !!userProfile.constraints.budgetRange && !!userProfile.constraints.timeAvailability;
      default:
        return false;
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 800, mx: 'auto', p: 3 }}>
      <Typography variant="h4" gutterBottom align="center">
        
        {t("Travel Profile")}
      </Typography>
      <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 4 }}>
        
        {t("Help us personalize your tour guide experience by sharing your travel preferences")}
      </Typography>

      {saveError && (
        <Alert
          severity="error"
          onClose={() => setSaveError(null)}
          sx={{ mb: 3 }}
        >
          {saveError}
        </Alert>
      )}

      <Stepper activeStep={activeStep} orientation="vertical">
        {steps.map((step, index) => (
          <Step key={step.label}>
            <StepLabel
              optional={
                index === steps.length - 1 ? (
                  <Typography variant="caption">{t("Last step")}</Typography>
                ) : null
              }
              StepIconComponent={() => (
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    bgcolor: isStepComplete(index) ? 'success.main' : 
                             activeStep === index ? 'primary.main' : 'grey.300',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {isStepComplete(index) ? <CheckCircle sx={{ fontSize: 16 }} /> : step.icon}
                </Box>
              )}
            >
              {step.label}
            </StepLabel>
            <StepContent>
              <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                {step.content}
              </Paper>
              <Box sx={{ mb: 2, mt: 2 }}>
                <Button
                  variant="contained"
                  onClick={index === steps.length - 1 ? handleSave : handleNext}
                  disabled={!isStepComplete(index) || isSaving}
                  sx={{ mr: 1 }}
                >
                  {isSaving ? (
                    <>
                      <CircularProgress size={16} sx={{ mr: 1 }} />
                      
                      {t("Saving...")}
                    </>
                  ) : index === steps.length - 1 ? (
                    t('Save Profile')
                  ) : (
                    t('Continue')
                  )}
                </Button>
                <Button
                  disabled={index === 0}
                  onClick={handleBack}
                  sx={{ mr: 1 }}
                >
                  
                  {t("Back")}
                </Button>
                {onClose && (
                  <Button onClick={onClose} color="secondary">
                    
                    {t("Cancel")}
                  </Button>
                )}
              </Box>
            </StepContent>
          </Step>
        ))}
      </Stepper>

      {activeStep === steps.length && (
        <Paper square elevation={0} sx={{ p: 3, mt: 3 }}>
          <Alert severity="success">
            <Typography variant="h6" gutterBottom>
              
              {t("Profile Complete!")}
            </Typography>
            <Typography variant="body2">
              
              {t("Your travel preferences have been saved and will help personalize your tour guide experience.")}
            </Typography>
          </Alert>
          <Button onClick={onClose} variant="contained" sx={{ mt: 2 }}>
            
            {t("Done")}
          </Button>
        </Paper>
      )}
    </Box>
  );
};
